import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import Grid from '@mui/material/Grid2';

import { 
  Character,
  Creature,
  TrueCreatureInstance,
  CustomParticipant,
  Encounter,
  EncounterParticipant,
} from "@dndModels";

import API from "@utilities/smart_api";
import CharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import ColorButtonBox from "@components/input/buttons/ColorButtonBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import CreatureParticipant from "@dndComponents/campaign/encounters/CreatureParticipant";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CustomParticipantInput from "@dndComponents/campaign/encounters/CustomParticipantInput";
import LoadingBox from "@components/input/LoadingBox";
import PageLinks from "@components/Navigation/PageLinks";
import FilterBox from "@dndComponents/filtering/FilterBox";
import SelectPlayerObjectBox from "@dndComponents/select/SelectPlayerObjectBox";
import AddCharacterParticipants from "@dndComponents/campaign/encounters/AddCharacterParticipants";

interface Props { 
  obj: Encounter;
  change_time: number;
  onChange: (changed: Encounter) => void;
}

const api = API.getInstance();
const char_util = CharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function AddParticipants({ 
  obj,
  change_time,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [go_load_characters, setGoLoadCharacters] = useState(false);
  const [loading_characters, setLoadingCharacters] = useState(false);
  const [last_change_time, setLastChangeTime] = useState(change_time);
  const [myObj, setObj] = useState(obj);
  const [page_num, setPageNum] = useState(0);
  const [creatures, setCreatures] = useState<Creature[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [characters_by_campaign, setCharactersByCampaign] = useState<{ campaign_id: string, characters: Character[] }[]>([]);
  const [selected_campaign_id, setSelectedCampaignId] = useState(obj.campaign_id);
  const [prev_campaign_id, setPrevCampaignId] = useState(obj.campaign_id);
  const [view, setView] = useState("Creatures");
  const [color, setColor] = useState("white");
  const [adding_creatures, setAddingCreatures] = useState<{ [creature_id: string]: { creature: Creature, count: number } }>({});
  const [filter, setFilter] = useState<any>({ is_dominant: true });

  const load = useCallback( async () => {
    const res = await api.getSetOfObjects({ data_types: [
      "creature",
    ]});
    setCreatures(res.creature as Creature[]);
    setLoading(false);
  }, []);

  const load_characters = useCallback( async () => {
    // console.log(selected_campaign_id);
    if (selected_campaign_id !== "") {
      if (obj.campaign_id === selected_campaign_id && obj.campaign) {
        setCharacters(obj.campaign.active_characters);
      } else {
        const this_one = characters_by_campaign.find(o => o.campaign_id === selected_campaign_id);
        if (this_one) {
          setCharacters(this_one.characters);
          // setGoLoadCharacters(true);
        } else {
          setLoadingCharacters(true);
          setCharacters([]);
          const filter: any = {};
          if (selected_campaign_id === "My Characters" && api.user) {
            filter.owner_id = api.user._id;
          } else {
            filter.campaign_id = selected_campaign_id;
          }
          const res = await api.getPlayerObjects({ data_type: "character", filter });
          const chars = (res as Character[]).filter(o => !o.is_archived);
          const chars_by_campaign = characters_by_campaign;
          chars_by_campaign.push({ campaign_id: selected_campaign_id, characters: chars });
          setCharactersByCampaign(chars_by_campaign);
          setCharacters(chars);
          setLoadingCharacters(false);
          setLastChangeTime(common_util.get_time_number());
        }
      }
    }
  }, [selected_campaign_id, obj, characters_by_campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
        load_characters();
      });
    } 
  }, [fetched, load]);

  useEffect(() => {
    if (last_change_time !== change_time) {
      // console.log("Updating");
      setObj(obj);
      setLastChangeTime(change_time);
    }
  }, [change_time, obj, setObj, last_change_time]);

  useEffect(() => {
    if (prev_campaign_id !== selected_campaign_id) {
      load_characters();
      setPrevCampaignId(selected_campaign_id);
    }
  }, [selected_campaign_id, prev_campaign_id, load_characters, setPrevCampaignId]);

  const addCreatures = async () => {
    const creature_ids = Object.keys(adding_creatures);
    let add_time = common_util.get_time_number();
    for (const creature_id of creature_ids) {
      const adding_creature = adding_creatures[creature_id];
      const creature = adding_creature.creature;
      await api.loadObject(creature);
      // debugger;
      let in_encounter = obj.participants.filter(o => o.creature_id === creature_id).length;
      const creature_instances: TrueCreatureInstance[] = [];
      for (let i = 0; i < adding_creature.count; ++i) {
        const new_creature_inst = await char_util.creatureToCreatureInstance({ creature });
        new_creature_inst._num = in_encounter + 1;
        new_creature_inst.name = new_creature_inst.name + (in_encounter > 0 ? ` ${in_encounter + 1}` : "");
        await char_util.buildMind(new_creature_inst, true);
        in_encounter++;
        creature_instances.push(new_creature_inst);
      }
      const new_participant = new EncounterParticipant();
      new_participant.participant = creature_instances[0];
      new_participant.participant_id = creature_instances[0].true_id;
      // new_participant.creature_instance_ids = creature_instances.map(o => o._id);
      new_participant.child_participants = creature_instances;
      new_participant.creature_id = creature._id;
      new_participant.owner_id = obj.owner_id;
      new_participant.owner_name = api.user ? api.user.name : "";
      new_participant.participant_type = "Creature";
      new_participant.color = color;
      new_participant.add_time = add_time;
      add_time++;
      myObj.participants.push(new_participant);
      if (creature_instances[0].lair_actions.length > 0 && 
        myObj.participants.filter(o => o.participant_type === "Lair Actions").length === 0) {
        const lair_actions_participant = new EncounterParticipant();
        lair_actions_participant.initiative = 20;
        lair_actions_participant.participant_id = "Lair Actions";
        lair_actions_participant.owner_id = myObj.owner_id;
        lair_actions_participant.owner_name = api.user ? api.user.name : "";
        lair_actions_participant.participant_type = "Lair Actions";
        lair_actions_participant.add_time = add_time;
        add_time++;
        myObj.participants.push(lair_actions_participant);
      }
    }
    updateEncounter(myObj, true);
  }

  const updateEncounter = (changed: Encounter, clear_adding = false) => {
    setObj(changed);
    if (clear_adding) {
      setAddingCreatures({});
    }
    onChange(changed);
  } 

  const updateFilter = (the_filter: any, startLetter = "") => {
    const new_filter = {...the_filter};
    console.log(the_filter);
    
    if (startLetter !== "") {
      new_filter["name*startsWith"] = startLetter;
    } else {
      delete new_filter["name*startsWith"];
    }

    setPageNum(0);
    setFilter(new_filter);
  }

  const renderCreatures = () => {
    const page_size = 7;
    const objects = creatures;
    let filtered = api.filter("creature", objects, filter) as Creature[];

    filtered = filtered.sort((a,b) => { 
      if (a.challenge_rating === b.challenge_rating) {
        return a.use_name.trim().localeCompare(b.use_name.trim()); 
      } else {
        return +a.challenge_rating < +b.challenge_rating ? -1 : 1;
      }
    });
    const page_count = Math.ceil(filtered.length / page_size);
    const filtered_and_paged = filtered.slice(page_size * page_num, page_size * (page_num + 1));
    
    return (
      <>
        <Grid size={12}
          container spacing={0} direction="row">
          <Grid size={10}>
            <FilterBox
              data_type='creature'
              dont_use_api
              the_filter={filter}
              all_objects={creatures}
              onChange={updateFilter}
              quick_fields={["creature_type","subtypes","min_cr","max_cr","is_dominant"]}
            />
          </Grid>
          <Grid size={2}>
            <ColorButtonBox 
              color={color}
              onChange={setColor}
            />
          </Grid>
        </Grid>
        <Grid size={12}>
          <Grid container spacing={0} direction="row">
            { filtered_and_paged.map(creature => {
              let adding = 0;
              const adding_creature = adding_creatures[creature._id];
              if (adding_creature) {
                adding = adding_creature.count;
              }
              return (
                <Grid size={12} key={creature._id}>
                  <CreatureParticipant
                    creature={creature}
                    disabled={false}
                    count={adding}
                    onChange={(count: number) => {
                      const new_adding_creatures = {...adding_creatures};
                      if (count === 0) {
                        delete new_adding_creatures[creature._id];
                      } else {
                        new_adding_creatures[creature._id] = { creature, count };
                      }
                      setAddingCreatures(new_adding_creatures);
                    }}
                  />
                </Grid>
              );
            }) }
            <Grid size={12}>
              <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
            </Grid>
            <Grid size={12}>
              { renderAddingCreatures() }
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }

  const renderAddingCreatures = () => {
    const creature_ids = Object.keys(adding_creatures);
    if (creature_ids.length > 0) {
      const adding_creatures_string = creature_ids.map(creature_id => {
        const adding_creature = adding_creatures[creature_id] as { creature: Creature, count: number };
        return adding_creature.creature.name + " x " + adding_creature.count;
      }).join(", ");
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={2}>Adding</Grid>
          <Grid size={8}>{ adding_creatures_string }</Grid>
          <Grid size={2}>
            <ButtonBox
              name="Add"
              onClick={() => {
                addCreatures().then((res: any) => {
                  updateEncounter(myObj, true);
                });
              }}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }

  const renderCharactersOuter = () => {
    return (
      <>
        <Grid size={12}
          container spacing={0} direction="row">
          <Grid size={12}>
            <SelectPlayerObjectBox 
              name="Campaign"
              data_type="campaign"
              extra_options={["My Characters"]}
              filter={{ owner_id: api.user ? api.user._id : "", is_archived: false }}
              value={selected_campaign_id}
              onChange={setSelectedCampaignId}
            />
          </Grid>
          <Grid size={10}>
            <FilterBox
              data_type='character'
              dont_use_api
              the_filter={filter}
              all_objects={characters}
              onChange={updateFilter}
              // log_it
              quick_fields={["game_class","subclass","race","subrace"]}
            />
          </Grid>
          <Grid size={2}>
            <ColorButtonBox 
              color={color}
              onChange={setColor}
            />
          </Grid>
        </Grid>
        { loading_characters ?
          <Grid size={12}><LoadingBox /></Grid>
        : renderCharacters()
        }
      </>
    );
  }

  const renderCharacters = () => {
    return (
      <AddCharacterParticipants 
        obj={obj} 
        characters={characters} 
        filter={filter} 
        change_time={change_time} 
        color={color} 
        onChange={onChange}
      />
    );
  }

  const renderCustom = () => {
    if (view === "Custom") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 10
            }}>
            <CustomParticipantInput 
              onAdd={(customParticipant: CustomParticipant, count: number) => {
                let add_time = common_util.get_time_number();
                const cps: CustomParticipant[] = [];
                for (let i = 0; i < count; ++i) {
                  const cp = customParticipant.clone();
                  cp.true_id = uuidv4().toString();
                  cps.push(cp);
                }
                const new_participant = new EncounterParticipant();
                new_participant.participant = cps[0];
                new_participant.participant_id = cps[0].true_id;
                new_participant.child_participants = cps;
                new_participant.owner_id = myObj.owner_id;
                new_participant.owner_name = api.user ? api.user.name : "";
                new_participant.participant_type = "Custom";
                new_participant.color = color;
                new_participant.add_time = add_time;
                add_time++;
                myObj.participants.push(new_participant);
                updateEncounter(myObj, true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ColorButtonBox 
              color={color}
              onChange={setColor}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <CenteredMenu
            options={["Creatures","Characters","Custom"]}
            selected={view}
            onSelect={(changed: string) => {
              setView(changed);
            }}
          />
        </Grid>
        { view === "Creatures" ? renderCreatures() :
          view === "Characters" ? renderCharactersOuter() :
          view === "Custom" && renderCustom() 
        }
      </Grid>
    );
  }
}